import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Loader2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabaseGet } from "@/utils/supabaseRest";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { cloneTemplate } from "@/services/flowTemplateService";

interface FlowTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  verticals: string[] | null;
  estimated_rpus: number | null;
  popularity: number | null;
}

interface TemplateGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Color-coded vertical pills. Falls back to slate for unknown verticals so
// new categories don't break the UI.
const verticalColor = (vertical: string): string => {
  const v = vertical.toLowerCase();
  if (v.includes("auto")) return "bg-blue-500/10 text-blue-300 border-blue-500/30";
  if (v.includes("home")) return "bg-amber-500/10 text-amber-300 border-amber-500/30";
  if (v.includes("health")) return "bg-rose-500/10 text-rose-300 border-rose-500/30";
  if (v.includes("life")) return "bg-purple-500/10 text-purple-300 border-purple-500/30";
  if (v.includes("loan") || v.includes("personal_loan"))
    return "bg-cyan-500/10 text-cyan-300 border-cyan-500/30";
  if (v.includes("debt")) return "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
  if (v.includes("mortgage")) return "bg-orange-500/10 text-orange-300 border-orange-500/30";
  return "bg-slate-700/50 text-slate-300 border-slate-600";
};

const formatRpus = (rpus: number | null): string => {
  if (rpus == null) return "$0.00";
  return `$${Number(rpus).toFixed(2)}`;
};

const formatVerticalLabel = (v: string): string =>
  v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function TemplateGallery({ open, onOpenChange }: TemplateGalleryProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [cloningId, setCloningId] = useState<string | null>(null);

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["flow-templates"],
    queryFn: async () => {
      const data = await supabaseGet<FlowTemplate[]>(
        'flow_templates?select=id,slug,name,description,category,verticals,estimated_rpus,popularity&is_public=eq.true&order=popularity.desc'
      );
      return data || [];
    },
    enabled: open,
    staleTime: 60_000,
  });

  const handleUseTemplate = async (templateId: string) => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to use a template",
      });
      return;
    }
    setCloningId(templateId);
    try {
      const { chatbotId } = await cloneTemplate(templateId, user.id);
      toast({
        title: "Template cloned",
        description: "Your new chatbot is ready to customize.",
      });
      onOpenChange(false);
      navigate(`/portal/flow/${chatbotId}`);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Clone failed",
        description: err?.message || "Could not clone template",
      });
    } finally {
      setCloningId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-400" />
            Flow Template Gallery
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Pick a battle-tested revenue routing flow and start earning in
            minutes. Each template clones into a fully editable chatbot.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mt-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-56 bg-slate-800" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400">Failed to load templates</p>
          </div>
        ) : !templates || templates.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No templates available yet</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mt-4">
            {templates.map((tpl) => {
              const isCloning = cloningId === tpl.id;
              return (
                <Card
                  key={tpl.id}
                  className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-colors p-5 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-white text-lg leading-snug flex-1">
                      {tpl.name}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-400 line-clamp-3 min-h-[3.75rem]">
                    {tpl.description || "No description provided"}
                  </p>

                  {tpl.verticals && tpl.verticals.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {tpl.verticals.map((v) => (
                        <span
                          key={v}
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${verticalColor(v)}`}
                        >
                          {formatVerticalLabel(v)}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-800 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">
                        Est. RPUS
                      </p>
                      <p className="text-2xl font-bold text-emerald-400 leading-none mt-1">
                        {formatRpus(tpl.estimated_rpus)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleUseTemplate(tpl.id)}
                      disabled={isCloning || cloningId !== null}
                      className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"
                    >
                      {isCloning ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                          Cloning...
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-1.5" />
                          Use This Template
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
